import './SpendConcentration.scss';
import React from "react";
import {useStores} from "../../../stores";
import {Button, Grid, TablePagination, Typography} from "@mui/material";
import {SpendConcentrationHelp} from "./SpendConcentrationHelp";
import {UNCATEGORIZED_LABEL, UNCATEGORIZED_VALUE} from "../../../constants";
import {TopOtherVisualization} from "./viz/TopOtherVisualization";
import {ParetoOverviewVisualization} from "./viz/ParetoOverviewVisualization";
import {observer} from "mobx-react-lite";
import {SpendConcentrationBreadcrumbs} from "./SpendConcentrationBreadcrumbs";
import {SpendConcentrationSearchComponent} from './SpendConcentrationSearchComponent';
import {
    SupplierConsolidationOpportunityModal
} from "../../../pages/opportunities-trello/SupplierConsolidationOpportunityModal";
import {Add, Check} from "@mui/icons-material";
import {runInAction} from "mobx";
import {MainPageFacade} from "../../../components/main/page/MainPageFacade.tsx";
import {HelpComponent} from "../../../components/help-component/HelpComponent.tsx";
import {CARD_CONFIG} from "../../../components/dashboard/DashboardCardConfigs.tsx";
import {MithraEchart} from "../../../services/echarts/MithraEchart.tsx";
import {EChartContextType} from "../../../services/echarts/EChartProvider.tsx";
import {EChartsType} from "echarts/types/dist/shared";
import {SCTransactionDetailTableComponent} from "./components/SCTransactionDetailTableComponent.tsx";

function initBarChart(echart: EChartContextType, root: HTMLElement, data: Array<Array<any>>): EChartsType {
    const chart = echart.init(root, 'mithra', {
        renderer: 'svg',
        height: 500,
    });
    const series = {
        name: 'Supplier spend',
        type: 'bar',
        barWidth: '60%',
        data: data,
    };
    const chartOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
            },
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: {show: true},
            },
        },
        grid: {
            bottom: '3%',
            left: '1%',
            right: '1%',
            containLabel: true,
        },
        series: [series],
        xAxis: {
            type: 'category',
            axisLabel: {
                rotate: 60,
                width: 150,
                overflow: 'truncate',
            },
        },
        yAxis: {
            type: 'value',
        },
    };
    chart.setOption(chartOption);
    return chart;
}

export const SpendConcentration: React.FC = observer(() => {
    const {
        spendConcentrationStore, opportunityStore,
        cmpStore, p
    } = useStores();
    const pageController = spendConcentrationStore.sCPageController;
    const filterController = pageController.filterController;
    let title = 'Spend per supplier';
    if (filterController.selectedCategoryLabel)
        // Or a breadcrumb here as well???
        title += ' ' + filterController.selectedCategoryLabel === UNCATEGORIZED_VALUE ? UNCATEGORIZED_LABEL : filterController.selectedCategoryLabel;

    const [isOpportunityModelOpen, setOpportunityModalOpen] = React.useState(false);

    const opportunityCreated = opportunityStore.hasOpportunity(cmpStore.cmpCat.selectedOpportunityId)

    return (
        <MainPageFacade
            title={CARD_CONFIG['koi-spend-concentration'].title}
            actionBarStart={<HelpComponent helpContent={<SpendConcentrationHelp/>}/>}
            className="koi-spend-concentration-page">
            <section className="page-item">
                <SupplierConsolidationOpportunityModal
                    isOpen={isOpportunityModelOpen}
                    setOpen={setOpportunityModalOpen}/>

                <Grid container justifyContent="space-evenly">
                    <Grid size={4}>
                        {
                            spendConcentrationStore.topValue && spendConcentrationStore.otherValue &&
                            <TopOtherVisualization
                                topValue={spendConcentrationStore.topValue}
                                otherValue={spendConcentrationStore.otherValue}
                            />
                        }
                    </Grid>
                </Grid>

                <Grid container justifyContent="center">
                    <Grid
                        size={{
                            md: 9,
                            xs: 12
                        }}>
                        <Grid container justifyContent="end">
                            <Grid size={8}>
                                <SpendConcentrationBreadcrumbs/>
                            </Grid>
                        </Grid>
                        {spendConcentrationStore.groupedData && <ParetoOverviewVisualization
                            data={spendConcentrationStore.groupedData}
                            setSelected={d => {
                                if (filterController.canSelectLevelDeeper) {
                                    filterController.selectNextCategoryDown(d.category);
                                } else {
                                    filterController.selectCategorySibling(d.category);
                                }
                            }}
                        />}
                    </Grid>
                    <Grid
                        size={{
                            md: 2,
                            xs: false
                        }}/>
                </Grid>

                <Grid container justifyContent="space-evenly" className="page-item">
                    <Grid size={3}>
                        <Typography variant="h6" align="center" className="pareto-chart-title">
                            {title}
                        </Typography>
                    </Grid>
                    <Grid size={3}>
                        <SpendConcentrationSearchComponent/>
                    </Grid>
                    {/*<Grid size={2}>*/}
                    {/*    <Button*/}
                    {/*        variant="outlined"*/}
                    {/*        disabled={!filterController.selectedCategoryLabel}*/}
                    {/*        color="primary"*/}
                    {/*        size="small"*/}
                    {/*        // endIcon={<Undo>All categories</Undo>}*/}
                    {/*        onClick={() => filterController.setSelectedCategory([])}>*/}
                    {/*        Show all categories*/}
                    {/*    </Button>*/}
                    {/*</Grid>*/}
                </Grid>

                <Grid container justifyContent="space-evenly" className='mb-4'>
                    <Grid size={8}>
                        {/*<p>*/}
                        {/*    Selection: {filterController.selectedCategoryKey}<br/>*/}
                        {/*    Suppliers in stats: {spendConcentrationStore.spendConcentrationSupplierRM.result?.data.count ?? 0} (possible overlap)*/}
                        {/*</p>*/}
                        <MithraEchart
                            initChart={(echart, root) => initBarChart(echart, root, spendConcentrationStore.echartData)}
                            data={spendConcentrationStore.echartData}
                            alwaysRerender={() => {
                            }}
                            onClick={(params) => {
                                filterController.setSupplierSearch(params.data[0]);
                                filterController.setSelectedSupplierSpend(params.data[0]);
                            }}
                        />
                        <TablePagination
                            count={spendConcentrationStore.spendConcentrationSupplierRM.result?.data.count ?? 0}
                            page={spendConcentrationStore.sCPage}
                            onPageChange={(_e, p) => spendConcentrationStore.setSCPage(p)}
                            rowsPerPage={spendConcentrationStore.sCPerPage}
                            onRowsPerPageChange={(e) => spendConcentrationStore.setSCPerPage(Number(e.target.value))}
                            showFirstButton
                            showLastButton
                            className='float-end'
                        />
                    </Grid>
                </Grid>

                {p.p.enableOpportunityTracker &&
                    <Grid container justifyContent="space-evenly">
                        <Grid>
                            <Button
                                variant="outlined"
                                startIcon={opportunityCreated ? <Check/> : <Add/>}
                                // disabled={!canCreateOpportunity}
                                onClick={() => runInAction(() => {
                                    opportunityStore.isEdit = false;

                                    opportunityStore.editingScOpportunity.title = '';
                                    opportunityStore.editingScOpportunity.id = `${new Date()}`;
                                    opportunityStore.editingScOpportunity.checked = false;
                                    const data = opportunityStore.editingScOpportunity.data;
                                    if (data.type === 'supplier_consolidation') {
                                        data.otherSupplierName = '';
                                        data.opportunitySize = 1000;
                                        data.notes = '';
                                    } else {
                                        console.warn('Experimental...')
                                    }
                                    setOpportunityModalOpen(true);
                                })}>
                                Add opportunity
                            </Button>
                        </Grid>
                    </Grid>
                }
            </section>

            <section className="table-section">
                <SCTransactionDetailTableComponent/>
            </section>
        </MainPageFacade>
    );
});
